/**
 * TransactionHistoryScreen.jsx
 * Detaillierte Transaktionshistorie für Mobile App
 */
import React, { useState, useEffect } from 'react';
import { 
  Wallet, Calendar, TrendingUp, TrendingDown, ChevronDown, ChevronUp,
  Clock, Check, X, RefreshCw, CreditCard, Dumbbell, Receipt
} from 'lucide-react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export const TransactionHistoryScreen = ({ portalToken, customerId, onNavigate }) => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedMonths, setExpandedMonths] = useState({});

  const getMonthName = (monthNum) => t(`transactions.month_${monthNum}`);

  useEffect(() => {
    if (portalToken) loadData();
  }, [portalToken]);

  const loadData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/portal/transactions/history`, {
        headers: { Authorization: `Bearer ${portalToken}` }
      });
      setData(response.data);
      if (response.data?.grouped_transactions?.length > 0) {
        setExpandedMonths({ [response.data.grouped_transactions[0].month]: true });
      }
    } catch (error) {
      console.error('Error loading transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleMonth = (month) => {
    setExpandedMonths(prev => ({ ...prev, [month]: !prev[month] }));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return d.toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit' });
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleTimeString('de-CH', { hour: '2-digit', minute: '2-digit' });
  };

  const formatCurrency = (amount) => {
    return `CHF ${(amount || 0).toFixed(2)}`;
  };

  const summary = data?.summary || {};

  return (
    <div className="p-4 space-y-4 pb-24" data-testid="transaction-history-screen">
      {/* Header */}
      <div className="flex justify-between items-center pt-2">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-500/30 to-emerald-500/20 flex items-center justify-center">
            <Receipt className="w-5 h-5 text-green-400" />
          </div>
          <div>
            <h1 className="text-white text-base font-bold">{t('transactions.title')}</h1>
            <p className="text-zinc-400 text-xs">{t('transactions.subtitle')}</p>
          </div>
        </div>
        <button 
          onClick={loadData}
          disabled={loading}
          className="p-2 bg-zinc-800 rounded-lg"
        >
          <RefreshCw className={`w-5 h-5 text-zinc-400 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {loading ? (
        <div className="bg-zinc-800/50 rounded-2xl p-8 text-center">
          <RefreshCw className="w-8 h-8 text-yellow-400 animate-spin mx-auto mb-3" />
          <p className="text-zinc-400 text-sm">{t('transactions.loading')}</p>
        </div>
      ) : !data || !data.grouped_transactions?.length ? (
        <div className="bg-zinc-800/50 rounded-2xl p-8 text-center">
          <Wallet className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
          <p className="text-zinc-400">{t('transactions.empty')}</p>
        </div>
      ) : (
        <>
          {/* Summary Card */}
          <div className="bg-zinc-800/50 rounded-2xl p-4" data-testid="summary-card">
            <h3 className="text-white font-medium mb-3">{t('transactions.overview')}</h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-zinc-900/50 rounded-xl p-3 text-center">
                <p className="text-2xl font-bold text-yellow-400">{summary.current_balance || 0}</p>
                <p className="text-zinc-500 text-xs">{t('transactions.points_balance')}</p>
              </div>
              <div className="bg-zinc-900/50 rounded-xl p-3 text-center">
                <p className="text-2xl font-bold text-white">{summary.total_trainings || 0}</p>
                <p className="text-zinc-500 text-xs">{t('transactions.trainings')}</p>
              </div>
              <div className="bg-zinc-900/50 rounded-xl p-3 text-center">
                <p className="text-xl font-bold text-green-400">+{summary.total_points_earned || 0}</p>
                <p className="text-zinc-500 text-xs">{t('transactions.earned')}</p>
              </div>
              <div className="bg-zinc-900/50 rounded-xl p-3 text-center">
                <p className="text-xl font-bold text-orange-400">-{summary.total_points_spent || 0}</p>
                <p className="text-zinc-500 text-xs">{t('transactions.spent')}</p>
              </div>
            </div>
          </div>

          {/* Monthly Groups */}
          {data.grouped_transactions.map((group) => {
            const [year, month] = group.month.split('-');
            const monthName = getMonthName(month);
            const isExpanded = expandedMonths[group.month];

            return (
              <div key={group.month} className="bg-zinc-800/50 rounded-2xl overflow-hidden">
                {/* Month Header */}
                <button
                  onClick={() => toggleMonth(group.month)}
                  className="w-full p-4 flex items-center justify-between"
                >
                  <div className="flex items-center gap-3">
                    <Calendar className="w-5 h-5 text-yellow-400" />
                    <span className="text-white font-medium">{monthName} {year}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2 text-xs">
                      <span className="text-zinc-400">{group.summary.training_count} 🏋️</span>
                      {group.summary.points_earned > 0 && (
                        <span className="text-green-400">+{group.summary.points_earned}</span>
                      )}
                    </div>
                    {isExpanded ? (
                      <ChevronUp className="w-4 h-4 text-zinc-500" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-zinc-500" />
                    )}
                  </div>
                </button>

                {/* Expanded Content */}
                {isExpanded && (
                  <div className="px-4 pb-4 space-y-4 border-t border-zinc-700/50">
                    {/* Appointments */}
                    {group.appointments?.length > 0 && (
                      <div className="pt-3">
                        <p className="text-zinc-400 text-xs font-medium mb-2 flex items-center gap-1">
                          <Dumbbell className="w-3 h-3" /> {t('transactions.trainings_label')}
                        </p>
                        <div className="space-y-2">
                          {group.appointments.map((apt, idx) => (
                            <div 
                              key={apt.id || idx}
                              className={`flex items-center justify-between p-3 rounded-xl ${
                                apt.status === 'cancelled' ? 'bg-red-500/10' : 'bg-zinc-900/50'
                              }`}
                            >
                              <div className="flex items-center gap-3">
                                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                                  apt.status === 'cancelled' ? 'bg-red-500/20' : 'bg-yellow-500/20'
                                }`}>
                                  {apt.status === 'cancelled' ? (
                                    <X className="w-4 h-4 text-red-400" />
                                  ) : (
                                    <Check className="w-4 h-4 text-yellow-400" />
                                  )}
                                </div>
                                <div>
                                  <p className="text-white text-sm font-medium">
                                    {apt.service_name || 'Training'}
                                  </p>
                                  <p className="text-zinc-500 text-xs">
                                    {formatDate(apt.start_time)} • {formatTime(apt.start_time)}
                                  </p>
                                </div>
                              </div>
                              <div className="text-right">
                                {apt.status === 'cancelled' ? (
                                  <span className="text-red-400 text-xs">{t('transactions.cancelled')}</span>
                                ) : apt.service_points > 0 ? (
                                  <span className="text-orange-400 text-sm">-{apt.service_points}</span>
                                ) : null}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Payments */}
                    {group.payments?.length > 0 && (
                      <div className="pt-2">
                        <p className="text-zinc-400 text-xs font-medium mb-2 flex items-center gap-1">
                          <CreditCard className="w-3 h-3" /> {t('transactions.payments_label')}
                        </p>
                        <div className="space-y-2">
                          {group.payments.map((payment, idx) => (
                            <div 
                              key={payment.id || idx}
                              className="flex items-center justify-between p-3 rounded-xl bg-zinc-900/50"
                            >
                              <div>
                                <p className="text-white text-sm font-medium">
                                  {payment.description || payment.type || t('transactions.payment')}
                                </p>
                                <p className="text-zinc-500 text-xs">{formatDate(payment.created_at)}</p>
                              </div>
                              <div className="text-right">
                                <p className="text-green-400 font-medium">{formatCurrency(payment.amount)}</p>
                                {payment.points_earned && (
                                  <p className="text-green-400 text-xs">+{payment.points_earned} P</p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Point Transactions */}
                    {group.point_transactions?.length > 0 && (
                      <div className="pt-2">
                        <p className="text-zinc-400 text-xs font-medium mb-2 flex items-center gap-1">
                          <TrendingUp className="w-3 h-3" /> {t('transactions.points_label')}
                        </p>
                        <div className="space-y-2">
                          {group.point_transactions.map((tx, idx) => (
                            <div 
                              key={tx.id || idx}
                              className="flex items-center justify-between p-3 rounded-xl bg-zinc-900/50"
                            >
                              <div>
                                <p className="text-white text-sm font-medium">
                                  {tx.description || tx.type || t('transactions.point_movement')}
                                </p>
                                <p className="text-zinc-500 text-xs">
                                  {formatDate(tx.created_at)}
                                  {tx.subscription_name && ` • ${tx.subscription_name}`}
                                </p>
                              </div>
                              <p className={`font-medium ${tx.points > 0 ? 'text-green-400' : 'text-orange-400'}`}>
                                {tx.points > 0 ? '+' : ''}{tx.points}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}
    </div>
  );
};

export default TransactionHistoryScreen;
